import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * A locked-out customer the automatic checks could not settle.
 *
 * Most recoveries never reach here. A BVN that matched and a liveness check
 * that passed finishes on its own, because a queue that exists to
 * rubber-stamp two agreeing checks gets rubber-stamped — and the customer
 * cannot run their business while it waits. What lands in this list is the
 * awkward remainder, almost always a photo the model would not pass.
 */
export interface RecoveryRequest {
  id: string;
  userId: string | null;
  /** What they typed to start. Worth reading: asking for recovery on an
   *  address the account no longer uses is the shape of both a genuine
   *  lockout and an attempt on somebody else's account. */
  identifierUsed: string;
  status: 'awaiting_review';
  attempts: number;
  identityVerifiedAt: string | null;
  livenessVerifiedAt: string | null;
  livenessConfidence: number | null;
  /** What the model said, since the reviewer never saw the attempt. */
  livenessFailure: string | null;
  ipAddress: string | null;
  deviceLabel: string | null;
  createdAt: string;
}

export const accountRecoveryApi = {
  pending: async () => {
    const { data } =
      await apiClient.get<ApiResponse<RecoveryRequest[]>>(
        '/admin/account-recovery/pending',
      );
    return data.data;
  },

  /**
   * Approving returns a one-time token, shown once and never stored in the
   * clear. Getting it to the customer is then a deliberate act on a channel
   * the reviewer has satisfied themselves about — which is the whole point
   * of a person being involved.
   */
  review: async (
    id: string,
    decision: 'approve' | 'reject',
    note?: string,
  ) => {
    const { data } = await apiClient.post<
      ApiResponse<{ status: string; recoveryToken?: string }>
    >(`/admin/account-recovery/${id}/review`, { decision, note });
    return data.data;
  },
};
